(() => {
  if (customElements.get('ps-startseiten-bilder')) return;
  class PsStartseitenBilder extends HTMLElement {
    connectedCallback() {
      this.abort?.abort();
      this.observer?.disconnect();
      clearTimeout(this.timer);
      this.abort = new AbortController();
      this.hero = this.closest('.ps-hero');
      this.controls = this.querySelector('.ps-startseiten-bilder__controls');
      if (!this.controls || !this.hero) return;
      this.interval = Math.max(3000, Math.min(5000, Number(this.dataset.interval) || 5000));
      this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.paused = this.reduced.matches || Boolean(window.Shopify?.designMode);
      this.visible = true;
      this.hovered = false;
      this.busy = false;

      const listen = (node, event, fn) => node.addEventListener(event, fn, { signal: this.abort.signal });
      listen(this.querySelector('[data-prev]'), 'click', () => this.select(-1));
      listen(this.querySelector('[data-next]'), 'click', () => this.select(1));
      listen(this.querySelector('[data-pills]'), 'click', event => {
        const button = event.target.closest('[data-motiv-index]');
        if (!button) return;
        this.paused = true;
        this.show(Number(button.dataset.motivIndex));
      });
      listen(this, 'keydown', event => {
        if (!['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
        event.preventDefault();
        this.select(event.key === 'ArrowRight' ? 1 : -1);
      });
      this.hero.addEventListener('click', event => {
        if (this.swiped && event.detail !== 0) {
          this.swiped = false;
          event.preventDefault();
          event.stopImmediatePropagation();
        }
      }, { capture: true, signal: this.abort.signal });
      listen(this.hero, 'pointerdown', event => {
        this.swiped = false;
        if (event.pointerType === 'touch' && !event.target.closest('a')) this.touch = { x: event.clientX, y: event.clientY };
      });
      listen(this.hero, 'pointercancel', () => { this.touch = null; });
      listen(this.hero, 'pointerup', event => {
        if (!this.touch || event.pointerType !== 'touch') return;
        const dx = event.clientX - this.touch.x;
        const dy = event.clientY - this.touch.y;
        this.touch = null;
        if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy) * 1.5) {
          this.swiped = true;
          this.select(dx < 0 ? 1 : -1);
        }
      });
      listen(this.hero, 'focusin', () => { this.paused = true; this.update(); });
      listen(this.hero, 'pointerenter', event => {
        if (event.pointerType !== 'touch') { this.hovered = true; this.schedule(); }
      });
      listen(this.hero, 'pointerleave', () => { this.hovered = false; this.schedule(); });
      listen(document, 'ps:saison:wechsel', () => this.refresh());
      listen(document, 'visibilitychange', () => this.schedule());
      listen(this.reduced, 'change', () => { if (this.reduced.matches) this.paused = true; this.update(); });
      listen(this.closest('.shopify-section') || this, 'shopify:block:select', event => {
        const index = this.slides.findIndex(slide => event.target.contains(slide));
        if (index >= 0) { this.paused = true; this.show(index); }
      });
      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(entries => {
          this.visible = entries[0].isIntersecting;
          this.schedule();
        }, { threshold: 0.1 });
        this.observer.observe(this.hero);
      }
      this.refresh();
    }
    disconnectedCallback() {
      clearTimeout(this.timer);
      this.abort?.abort();
      this.observer?.disconnect();
      this.request = (this.request || 0) + 1;
    }
    refresh() {
      this.request = (this.request || 0) + 1;
      this.slides = [...this.querySelectorAll('[data-bild]')];
      this.index = Math.max(0, this.slides.findIndex(slide => slide.classList.contains('is-active')));
      this.busy = false;
      this.controls.hidden = this.slides.length < 2;
      const pills = this.querySelector('[data-pills]');
      if (pills.children.length !== this.slides.length) {
        pills.replaceChildren(...this.slides.map((slide, index) => {
          const button = document.createElement('button');
          button.type = 'button';
          button.dataset.motivIndex = index;
          button.setAttribute('aria-label', this.dataset.choose.replace('__NUMBER__', index + 1));
          const mark = document.createElement('span');
          mark.setAttribute('aria-hidden', 'true');
          button.append(mark);
          return button;
        }));
      }
      this.slides.forEach((slide, index) => slide.setAttribute('aria-hidden', String(index !== this.index)));
      this.update();
    }
    update() {
      this.querySelectorAll('[data-motiv-index]').forEach(button => {
        button.setAttribute('aria-pressed', String(Number(button.dataset.motivIndex) === this.index));
      });
      this.schedule();
    }
    schedule() {
      clearTimeout(this.timer);
      if (!this.isConnected || this.paused || this.hovered || !this.visible || document.hidden || this.busy || this.slides.length < 2) return;
      this.timer = setTimeout(() => this.show((this.index + 1) % this.slides.length, true), this.interval);
    }
    select(direction) {
      if (this.slides.length < 2) return;
      this.paused = true;
      this.show((this.index + direction + this.slides.length) % this.slides.length);
    }
    async show(index, automatic = false) {
      clearTimeout(this.timer);
      const request = this.request = (this.request || 0) + 1;
      if (!this.slides[index]) return;
      const img = this.slides[index].querySelector('img');
      this.busy = true;
      if (img) {
        img.loading = 'eager';
        try { await img.decode(); } catch {
          if (this.request !== request || !this.isConnected) return;
          this.busy = false;
          if (automatic && (index + 1) % this.slides.length !== this.index) {
            this.show((index + 1) % this.slides.length, true);
          } else this.update();
          return;
        }
      }
      if (!this.isConnected || request !== this.request) return;
      if (automatic && (this.paused || this.hovered || !this.visible || document.hidden)) {
        this.busy = false;
        this.update();
        return;
      }
      this.slides.forEach((slide, i) => {
        slide.classList.toggle('is-active', i === index);
        slide.setAttribute('aria-hidden', String(i !== index));
        slide.inert = i !== index;
      });
      this.index = index;
      this.busy = false;
      this.update();
    }
  }
  customElements.define('ps-startseiten-bilder', PsStartseitenBilder);
})();
